function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useMemo } from 'react';
import GenericError from "../../../components/GenericError/GenericError";
import RestfulWrapper from "../../../components/RestfulWrapper/RestfulWrapper";
import { useFeatureFlags } from "../../../hooks/useFeatureFlags";
import GraphWidgetLayout from "../GraphWidget/GraphWidgetLayout";
import GraphWidgetTopCard from "../GraphWidget/GraphWidgetTopCard";
import { Spinner, Typography, WithInfoIcon } from 'seagull';
import GraphWidgetBottomCard from "../GraphWidget/GraphWidgetBottomCard";
import styles from "./SalesWidget.module.scss";
import { useIoContext, useStore, useStoreActions } from "../../../contexts";
import { selectors } from "../../../store/state-selectors";
import { SalesSummaryStatus } from "../../../models";
import ConditionalWrapper from "../../../components/ConditionalWrapper/ConditionalWrapper";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { commonBarProps, commonCartesianGridProps, commonXAxisProps } from "../charts/commonChartProps";
import CustomLegend from "../charts/CustomLegend/CustomLegend";
import colors from 'seagull/foundations/colors.module.scss';
import CustomXAxisTick from "../charts/CustomXAxisTick/CustomXAxisTick";
import { format } from 'date-fns';
import { utils } from "../../../utils";
import CustomTooltip from "../charts/CustomTooltip/CustomTooltip";
import CustomBarShape from "../charts/CustomBarShape/CustomBarShape";
import { toDatestampString } from "../../../models/time";
import Decimal from 'decimal.js';
import { numberAndUnit } from "../../../infra/utils";
import { getXAxisTextForQuarterlyDate } from "../charts/charts.utils";
import { getLastXMonths } from "../../../utils/date.utils";
const statusesOrder = [SalesSummaryStatus.Approved, SalesSummaryStatus.Reviewed, SalesSummaryStatus.Sent, SalesSummaryStatus.Canceled, SalesSummaryStatus.Terminated];
const statusToText = {
  [SalesSummaryStatus.Approved]: 'Signed',
  [SalesSummaryStatus.Reviewed]: 'Reviewed',
  [SalesSummaryStatus.Sent]: 'Not reviewed',
  [SalesSummaryStatus.Canceled]: 'Withdrawn',
  [SalesSummaryStatus.Terminated]: 'Terminated'
};
const statusToColor = {
  [SalesSummaryStatus.Approved]: colors.blue700,
  [SalesSummaryStatus.Reviewed]: colors.purple700,
  [SalesSummaryStatus.Sent]: colors.purple300,
  [SalesSummaryStatus.Canceled]: colors.dark200,
  [SalesSummaryStatus.Terminated]: colors.red500
};
const renderBar = status => /*#__PURE__*/React.createElement(Bar, _extends({}, commonBarProps, {
  key: status,
  dataKey: item => item.statuses[status].total.toNumber(),
  stackId: "a",
  fill: statusToColor[status],
  shape: /*#__PURE__*/React.createElement(CustomBarShape, {
    getHasRadius: item => {
      const statusIndex = statusesOrder.indexOf(status);
      const statusesAbove = statusesOrder.slice(statusIndex + 1);
      return !statusesAbove.some(s => item.statuses[s].total.gt(0));
    },
    getTestId: item => "".concat(status, "-for-").concat(toDatestampString(item.date))
  })
}));
export const renderTooltip = function renderTooltip(testid) {
  let additionalProps = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return /*#__PURE__*/React.createElement(CustomTooltip, _extends({}, additionalProps, {
    skin: "dark",
    testid: "".concat(testid, "-tooltip"),
    getTitle: (label, payload) => {
      if (!payload || !payload.length) return '';
      const item = payload[0].payload;
      return format(item.date, 'MMMM');
    },
    getItems: payload => {
      if (!payload || !payload.length) return;
      const item = payload[0].payload;
      return statusesOrder.filter(s => item.statuses[s].total.gt(0)).map(s => ({
        id: s,
        label: "".concat(utils.formatNumberWithCommas(item.statuses[s].total), " ").concat(statusToText[s]),
        color: statusToColor[s]
      }));
    },
    getBody: payload => {
      if (!payload || !payload.length) return null;
      const item = payload[0].payload;
      const totalProposals = Object.values(item.statuses).reduce((acc, val) => acc.add(val.total), new Decimal(0)).toNumber();
      return "Out of ".concat(numberAndUnit(totalProposals, 'proposal'), " sent:");
    }
  }));
};
export function getLegendItems(data) {
  return statusesOrder.filter(status => data.some(item => item.statuses[status].total.gt(0))).map(status => ({
    status,
    label: statusToText[status],
    color: statusToColor[status]
  }));
}
function SalesWidgetChart(_ref) {
  let {
    testid,
    data,
    isTestMode
  } = _ref;
  const barChartProps = isTestMode ? {
    height: 100,
    width: 100
  } : {};
  const legendItems = useMemo(() => getLegendItems(data), [data]);
  return /*#__PURE__*/React.createElement("div", {
    "data-testid": testid,
    className: styles.chart
  }, /*#__PURE__*/React.createElement(ConditionalWrapper, {
    condition: !isTestMode,
    wrapper: children => /*#__PURE__*/React.createElement(ResponsiveContainer, null, children)
  }, /*#__PURE__*/React.createElement(BarChart, _extends({}, barChartProps, {
    data: data,
    barCategoryGap: "10%",
    margin: {
      top: 20,
      left: 16,
      right: 16
    }
  }), /*#__PURE__*/React.createElement(CartesianGrid, commonCartesianGridProps), /*#__PURE__*/React.createElement(Legend, {
    content: /*#__PURE__*/React.createElement(CustomLegend, {
      testid: "".concat(testid, "-legend"),
      items: legendItems,
      gap: 16
    })
  }), /*#__PURE__*/React.createElement(XAxis, _extends({}, commonXAxisProps, {
    dataKey: item => item.date,
    height: 49,
    tick: /*#__PURE__*/React.createElement(CustomXAxisTick, {
      tickHeight: 30,
      getText: getXAxisTextForQuarterlyDate
    })
  })), /*#__PURE__*/React.createElement(YAxis, {
    axisLine: false,
    tickLine: false,
    width: 32,
    tickFormatter: value => value === 0 ? '' : utils.formatNumberWithCommas(value),
    fontSize: 12
  }), /*#__PURE__*/React.createElement(Tooltip, {
    cursor: false,
    isAnimationActive: false,
    content: renderTooltip(testid)
  }), statusesOrder.map(renderBar))));
}
const TopCard = _ref2 => {
  let {
    label,
    info,
    value,
    loading,
    testid
  } = _ref2;
  return /*#__PURE__*/React.createElement(GraphWidgetTopCard, {
    "data-testid": testid
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.topCard
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(WithInfoIcon, {
    info: info
  }, /*#__PURE__*/React.createElement(Typography, null, label))), /*#__PURE__*/React.createElement(Typography, {
    variant: "h5",
    weight: "normal",
    "data-testid": "".concat(testid, "-value")
  }, loading ? /*#__PURE__*/React.createElement(Spinner, {
    size: 30,
    "data-testid": "".concat(testid, "-loading")
  }) : value)));
};
function getMockChartData(now) {
  return getLastXMonths(now, 12).map(date => ({
    date,
    statuses: {
      [SalesSummaryStatus.Sent]: {
        total: new Decimal(2)
      },
      [SalesSummaryStatus.Approved]: {
        total: new Decimal(3)
      },
      [SalesSummaryStatus.Terminated]: {
        total: new Decimal(0)
      },
      [SalesSummaryStatus.Canceled]: {
        total: new Decimal(1)
      },
      [SalesSummaryStatus.Reviewed]: {
        total: new Decimal(1)
      }
    }
  }));
}
function MockSalesWidgetChart(_ref3) {
  let {
    isTestMode
  } = _ref3;
  const {
    getNow
  } = useIoContext();
  const data = useMemo(() => getMockChartData(getNow()), []);
  return /*#__PURE__*/React.createElement(SalesWidgetChart, {
    data: data,
    testid: "sales-widget-mock-chart",
    isTestMode: isTestMode
  });
}
function SalesWidgetContent(_ref4) {
  let {
    loading,
    data,
    isTestMode
  } = _ref4;
  const hasSentAProposal = useStore(selectors.hasSentAProposal);
  const areCardsLoading = !data;
  return /*#__PURE__*/React.createElement(GraphWidgetLayout, {
    title: "Sales",
    loading: loading,
    bottomHeight: "l",
    testid: "sales-widget"
  }, /*#__PURE__*/React.createElement(TopCard, {
    loading: areCardsLoading,
    value: data ? data.formattedNumProposalsSentThisYear() : '',
    testid: "sales-widget-proposals-sent-this-year",
    label: "Proposals sent this year",
    info: "The total number of proposals you've sent through Anchor so far this year (Since Jan 1)."
  }), /*#__PURE__*/React.createElement(TopCard, {
    loading: areCardsLoading,
    value: data ? data.formattedMedianTimeToSign() : '',
    testid: "sales-widget-median-time-to-sign",
    label: "Median time to sign",
    info: "The median time it takes for clients to sign proposals within the past 90 days."
  }), /*#__PURE__*/React.createElement(GraphWidgetBottomCard, {
    "data-testid": "monthly-sales".concat(areCardsLoading ? '-loading' : ''),
    loading: areCardsLoading,
    loadingMsg: "Loading data...",
    empty: data && data.isEmpty() && !hasSentAProposal,
    emptyMsg: "Here you will view your sales over time",
    loadingOrEmptyBg: () => /*#__PURE__*/React.createElement(MockSalesWidgetChart, {
      isTestMode: isTestMode
    })
  }, data && /*#__PURE__*/React.createElement(SalesWidgetChart, {
    data: data.calendar(),
    testid: "sales-widget-chart",
    isTestMode: isTestMode
  })));
}
export default function SalesWidget(_ref5) {
  let {
    isTestMode
  } = _ref5;
  const initialData = useStore(state => state.salesSummary.data);
  const {
    fetchSalesSummary
  } = useStoreActions();
  const {
    SALES_WIDGET
  } = useFeatureFlags();
  return !SALES_WIDGET ? null : /*#__PURE__*/React.createElement(RestfulWrapper, {
    initialData: initialData,
    fetch: fetchSalesSummary,
    renderChild: _ref6 => {
      let {
        data,
        loading
      } = _ref6;
      return /*#__PURE__*/React.createElement(SalesWidgetContent, {
        loading: loading,
        data: data,
        isTestMode: isTestMode
      });
    },
    renderLoader: () => /*#__PURE__*/React.createElement(SalesWidgetContent, {
      loading: true,
      isTestMode: isTestMode
    }),
    renderError: () => /*#__PURE__*/React.createElement(GenericError, {
      testid: "widget-error"
    })
  });
}